import { useContext, useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { useGenerateQRCode } from '@/modules/annotator/casePresentation/patientDownloadable/hooks/useGenerateQRCode';
import { QRPrintDownloadContent } from '@/modules/annotator/casePresentation/patientDownloadable/PreviewDialog/QRPrintDownloadContent';
import { useResetConfidenceScoreWhenPatientChanges } from '@/modules/annotator/findingSidebar/confidenceScoreFilter/helpers';
import type { Patient } from '@/modules/annotator/types';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import { useCornerstoneInitialization } from '@/utils/cornerstone/cornerstoneAtoms';

import { ExternalPatientContext } from '../constants';

interface Props {
  isLoading: boolean;
  visitSummaries?: TVisitSummary[];
}

export const ExternalQRPrintDownload = ({
  isLoading,
  visitSummaries,
}: Props) => {
  useCornerstoneInitialization();
  useResetConfidenceScoreWhenPatientChanges();

  const { imageId, patientId } = useContext(ExternalPatientContext);
  const { generateQRCode } = useGenerateQRCode();
  const reduxPatients = useSelector(
    (state: { annotator?: { patients?: Record<string, Patient> } }) =>
      state.annotator?.patients,
  );
  const activeImage = useMemo(() => {
    const images = Object.values(
      reduxPatients?.[patientId ?? '']?.images ?? {},
    );
    return images?.find((image) => image.imageId === imageId);
  }, [imageId, patientId, reduxPatients]);

  useEffect(() => {
    if (!isLoading && activeImage) {
      generateQRCode();
    }
    // Disabling exhaustive deps warning because we only want to generate
    // QR code once upon opening the modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImage, isLoading]);

  return (
    <QRPrintDownloadContent
      isLoading={isLoading}
      mode="light"
      visitSummaries={visitSummaries}
    />
  );
};
